import { DataGrid } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import { Search } from '@mui/icons-material'
import { databaseLocation } from '../../dummyData'
import { useState, useEffect, useRef } from 'react'
import Axios from 'axios'
import { TextField, InputAdornment } from '@mui/material'

export default function LogsPage({ setShowLoginPopup, showLoginPopup }) {
	const [logs, setLogs] = useState([])
	const [filteredLogs, setFilteredLogs] = useState([])
	const [searchText, setSearchText] = useState('')
	const [loading, setLoading] = useState(true)
	const prevLoginPopupRef = useRef(false)
	const justLoggedInRef = useRef(false)

	// Set withCredentials to send session cookies
	useEffect(() => {
		Axios.defaults.withCredentials = true
	}, [])

	// Fetch logs on mount and when search text changes
	useEffect(() => {
		let isMounted = true
		setLoading(true)

		const fetchLogs = async () => {
			try {
				const url = searchText.trim()
					? `${databaseLocation}/log/?search=${encodeURIComponent(searchText.trim())}`
					: `${databaseLocation}/log/`

				const response = await Axios.get(url)
				if (isMounted) {
					setLogs(response.data)
					setFilteredLogs(response.data)
				}
			} catch (error) {
				console.error('Error fetching logs:', error)
				if (isMounted) {
					// Handle 401 Unauthorized - show login modal only if we didn't just log in
					if (error.response && error.response.status === 401) {
						// Don't show popup if we just logged in (give it time to establish)
						if (!justLoggedInRef.current && setShowLoginPopup) {
							setShowLoginPopup(true)
						}
					}
					setLogs([])
					setFilteredLogs([])
				}
			} finally {
				if (isMounted) {
					setLoading(false)
				}
			}
		}

		fetchLogs()

		return () => {
			isMounted = false
		}
	}, [searchText, setShowLoginPopup])

	// Refetch logs when login popup closes (after successful login)
	useEffect(() => {
		// If popup was open and now closed, refetch logs
		if (prevLoginPopupRef.current === true && showLoginPopup === false) {
			// Mark that we just logged in to prevent immediate popup on 401
			justLoggedInRef.current = true

			// Add a delay to ensure session cookie is set and saved
			const timeoutId = setTimeout(() => {
				setLoading(true)
				const fetchLogs = async () => {
					try {
						const url = searchText.trim()
							? `${databaseLocation}/log/?search=${encodeURIComponent(
									searchText.trim()
							  )}`
							: `${databaseLocation}/log/`

						const response = await Axios.get(url)
						setLogs(response.data)
						setFilteredLogs(response.data)
						// Reset the flag after successful fetch
						justLoggedInRef.current = false
					} catch (error) {
						console.error('Error fetching logs:', error)
						if (error.response && error.response.status === 401) {
							// Only show popup again if we've waited long enough
							setTimeout(() => {
								justLoggedInRef.current = false
								if (setShowLoginPopup) {
									setShowLoginPopup(true)
								}
							}, 1000)
						}
						setLogs([])
						setFilteredLogs([])
					} finally {
						setLoading(false)
					}
				}
				fetchLogs()
			}, 500) // Longer delay to ensure session cookie is set and saved

			return () => clearTimeout(timeoutId)
		}
		prevLoginPopupRef.current = showLoginPopup
	}, [showLoginPopup, searchText, setShowLoginPopup, databaseLocation])

	const handleSearchChange = (event) => {
		setSearchText(event.target.value)
	}

	const formatTimestamp = (timestamp) => {
		if (!timestamp) return ''
		try {
			const date = new Date(timestamp)
			return date.toLocaleString('de-DE', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
			})
		} catch (error) {
			return timestamp
		}
	}

	const columns = [
		{
			// CHANGED: Feldname wie im Backend
			field: 'Timestamp',
			headerName: 'Timestamp',
			width: 200,
			renderCell: (params) => {
				// CHANGED: Timestamp mit großem T
				return <span>{formatTimestamp(params.row.Timestamp)}</span>
			},
		},
		{
			// CHANGED: Feldname wie im Backend
			field: 'Message',
			headerName: 'Message',
			width: 800,
			flex: 1,
		},
	]

	return (
		<div className='p-5'>
			<div className='flex justify-between mb-5 flex-wrap gap-5 flex-col md:flex-row items-start md:items-center'>
				<h2 className='m-0 text-gray-800 text-2xl font-semibold w-full md:w-auto'>Logs</h2>
				<TextField
					placeholder='Search logs...'
					variant='outlined'
					size='small'
					value={searchText}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<Search />
							</InputAdornment>
						),
					}}
					sx={{ minWidth: 300, marginBottom: 2 }}
				/>
			</div>

			{loading ? (
				<div className='py-10 text-center text-gray-600 text-base'>Loading logs...</div>
			) : (
				<Box sx={{ height: 600, width: '100%' }}>
					<DataGrid
						rows={filteredLogs}
						// CHANGED: ID-Spalte aus DB verwenden (falls vorhanden)
						getRowId={(row) => row.ID || row.id || row.logId || Math.random()}
						columns={columns}
						pageSize={25}
						rowsPerPageOptions={[10, 25, 50, 100]}
						disableSelectionOnClick
						sx={{ paddingLeft: '5px' }}
					/>
				</Box>
			)}
		</div>
	)
}
